import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { captureError } from "@/lib/sentry";
import { ChatCircleDotsIcon, XIcon, PaperPlaneRightIcon, LightningIcon } from "@phosphor-icons/react";
import AnswerSources from "@/components/AnswerSources";
import RichText from "@/components/RichText";

// Context-aware floating "Ask Gamly" chat overlay.
// Talks to POST /api/watch/{gameId}/ask which injects live game context + the user's personality.
export default function AskGamlyOverlay({ gameId, language, persona, openSignal, suggestions = [] }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const msgIdRef = useRef(0);
  const nextMsgId = () => ++msgIdRef.current;

  useEffect(() => {
    if (openSignal) setOpen(true);
  }, [openSignal]);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (preset) => {
    const text = (typeof preset === "string" ? preset : input).trim();
    if (!text || sending) return;
    if (typeof preset !== "string") setInput("");
    setMessages((m) => [...m, { id: nextMsgId(), role: "user", text }]);
    setSending(true);
    try {
      const res = await api.post(`/watch/${gameId}/ask`, { message: text, language });
      setMessages((m) => [...m, { id: nextMsgId(), role: "assistant", text: res.data.text, sources: res.data.sources }]);
    } catch (err) {
      captureError("ask gamly overlay failed:", err);
      const limited = err?.response?.status === 402;
      setMessages((m) => [...m, { id: nextMsgId(), role: "assistant", text: limited ? "You've used today's free questions — upgrade to Pro Fan for unlimited answers." : "Couldn't reach Gamly. Try again." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="watch-chat-toggle"
          className="fixed bottom-20 right-6 z-50 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0A84FF] hover:bg-blue-600 text-white font-black text-sm uppercase tracking-wide shadow-2xl shadow-[#0A84FF]/30 transition-transform hover:scale-105"
        >
          <ChatCircleDotsIcon weight="fill" size={20} /> Ask Gamly
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[400px] h-[70vh] max-h-[560px] flex flex-col rounded-xl bg-[#0d0d0d] border border-white/15 shadow-2xl overflow-hidden" data-testid="watch-chat-panel">
          <div className="px-4 py-3 flex items-center justify-between border-b border-white/10 bg-[#121212]">
            <div className="flex items-center gap-2">
              {persona && <persona.icon weight="fill" size={18} className="text-[#0A84FF]" />}
              <div>
                <p className="font-display font-bold text-sm uppercase tracking-tight leading-none">Ask Gamly</p>
                <p className="text-[10px] text-white/45 mt-0.5">{persona?.label || "Live"} · this game</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="watch-chat-close" className="text-white/40 hover:text-white"><XIcon size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-white/40 text-xs mt-6" data-testid="watch-chat-empty">
                <LightningIcon size={26} className="mx-auto mb-2 text-[#0A84FF]" />
                Ask anything about this game — momentum, players, what to watch next.
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                  m.role === "user" ? "bg-[#0A84FF] text-white" : "bg-white/[0.06] text-white/90 border border-white/10"
                }`}>
                  <RichText text={m.text} />
                  <AnswerSources sources={m.sources} testId={`overlay-sources-${m.id}`} />
                </div>
              </div>
            ))}
            {sending && <div className="text-[11px] font-bold uppercase tracking-wider text-[#0A84FF]" data-testid="watch-chat-thinking">Gamly thinking…</div>}
            <div ref={endRef} />
          </div>

          {suggestions.length > 0 && (
            <div className="px-3 pt-2 pb-1 flex gap-1.5 overflow-x-auto border-t border-white/5" data-testid="ask-suggestions" style={{ scrollbarWidth: "none" }}>
              {suggestions.map((s, i) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  disabled={sending}
                  data-testid={`ask-suggestion-${i}`}
                  className="shrink-0 px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/15 text-[11px] text-white/70 hover:text-white hover:border-[#0A84FF]/60 whitespace-nowrap disabled:opacity-40 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="p-3 border-t border-white/10 bg-[#121212] flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about this game…"
              data-testid="watch-chat-input"
              className="flex-1 bg-black border border-white/15 rounded-md px-3 py-2 text-sm focus:border-[#0A84FF] outline-none"
            />
            <button onClick={() => send()} disabled={sending} data-testid="watch-chat-send" className="px-3 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white disabled:opacity-50">
              <PaperPlaneRightIcon weight="fill" size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
